import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ChatFormOperation, IChatForm } from '@nimbus-global/features/chat-form/models/chat-form.model';
import { IChatFormFieldAction, IChatFormValueUpdate } from '@nimbus-global/features/chat-form/models/chat-form-resolver.model';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { ManageRouteMode } from '@nimbus-global/features/manage/model/manage-route.model';
import { CompanionReferenceType, ICompanionReference } from '@nimbus-library/features/companion-chat/models/companion-conversation.model';
import { IChatFormEntityHandler } from '@nimbus-library/features/companion-chat/services/form-entity-handlers/i-chat-form-entity-handler';
import { IOptionItem } from '@nimbus-library/features/shared/models/interfaces/i-option-item';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { LinkType } from '@nimbus-global/features/manage/components/organization-unit/domain/link/link-type.enum';
import { ToOrganizationUnitSelectListModelMapper } from '@nimbus-global/features/shared/services/api/organization-unit/domain/to-organization-unit-select-list-model.mapper';
import { BotConfigurationService } from '@nimbus-global/features/manage/services/bot-configuration.service';
import { VirtualUserService } from '@nimbus-global/features/manage/services/virtual-user.service';
import { TenantOrganizationUnitLookupService } from '@nimbus-global/features/shared/services/api/organization-unit/tenant-organization-unit-lookup.service';
import { GroupSelectionLookupService } from '@nimbus-admin/features/organization-unit/services/group-selection-lookup.service';
import { OrganizationUnitLinkService } from '@nimbus-admin/features/organization-unit/services/organization-unit-link.service';
@Injectable({ providedIn: 'root' })
export class VirtualUserChatFormEntityHandler implements IChatFormEntityHandler {
  private readonly nameMaxLength = 256;
  public readonly entity = CompanionReferenceType.VirtualUser;
  public readonly formDescription = TranslationKeys.CompanionVirtualUserFormDescription;
  public readonly formValueUpdate$: Observable<IChatFormValueUpdate>;

  public constructor(
    private readonly organizationUnitLookupService: TenantOrganizationUnitLookupService,
    private readonly botConfigurationService: BotConfigurationService,
    private readonly virtualUserService: VirtualUserService,
    private readonly groupSelectionLookupService: GroupSelectionLookupService,
    private readonly organizationUnitLinkService: OrganizationUnitLinkService,
  ) {
    this.formValueUpdate$ = this.initializeFormValueUpdate();
  }

  public resolveReferenceLink(reference: ICompanionReference): Observable<IChatFormFieldAction> {
    const tenantId = reference.tenantId;
    const organizationUnitId = reference.organizationUnitId;
    if (reference.kind !== this.entity || !reference.id || !tenantId || !organizationUnitId) {
      return of(null);
    }
    return this.groupSelectionLookupService.getTenantSelection(tenantId).pipe(
      switchMap((tenantSelection) => {
        if (!tenantSelection) {
          return of(null);
        }
        return this.organizationUnitLookupService.getOrganizationUnits(tenantId, [NimbusPermission.VirtualUsersUpdate]).pipe(
          take(1),
          map((organizationUnits) => {
            if (!organizationUnits.some((unit) => unit.id === organizationUnitId)) {
              return null;
            }
            const link = this.organizationUnitLinkService.getLinkByType(LinkType.VirtualUser);
            return {
              icon: ICONS.EXTERNAL_LINK,
              tooltip: TranslationKeys.GeneralVirtualUser,
              routerLink: [...link.pathFragments, reference.id, ManageRouteMode.Edit],
              queryParams: {
                ...(tenantSelection.partnerId ? { partnerId: tenantSelection.partnerId } : {}),
                tenantId,
                organizationUnitId,
              },
            } satisfies IChatFormFieldAction;
          }),
        );
      }),
    );
  }

  public resolveReferenceOptions(
    referenceType: string,
    form: IChatForm,
    currentValues?: Record<string, unknown>,
  ): Observable<IOptionItem[]> {
    const selectedTenantId = currentValues?.tenantId as string;
    const selectedOrganizationUnitId = currentValues?.organizationUnitId as string;
    if (referenceType === CompanionReferenceType.Tenant) {
      return this.groupSelectionLookupService.getTenantOptions(selectedTenantId);
    }
    if (referenceType === CompanionReferenceType.OrganizationUnit) {
      if (!selectedTenantId) {
        return of([]);
      }
      const includedIds = form.operation === ChatFormOperation.Create ? [] : [selectedOrganizationUnitId];
      return this.organizationUnitLookupService.getOrganizationUnits(selectedTenantId, [this.getPermission(form)], includedIds).pipe(
        map((ous) => {
          const mapper = new ToOrganizationUnitSelectListModelMapper();
          return form.operation === ChatFormOperation.Create
            ? mapper.forCreateDropDownWithoutServices(ous)
            : mapper.forEditDropDown(ous, selectedOrganizationUnitId, false);
        }),
      );
    }
    if (!selectedTenantId || !selectedOrganizationUnitId) {
      return of([]);
    }
    switch (referenceType) {
      case CompanionReferenceType.BotConfiguration:
        return this.botConfigurationService.botConfigurationsChanged$.pipe(
          startWith(null),
          switchMap(() =>
            this.organizationUnitLookupService.getOrganizationUnitWithParents(selectedTenantId, selectedOrganizationUnitId, [
              this.getPermission(form),
            ]),
          ),
          switchMap((ous) => (ous.length ? this.botConfigurationService.getBotConfigurations(ous) : of([]))),
        );
      case CompanionReferenceType.VirtualUser:
        return this.organizationUnitLookupService
          .getOrganizationUnitWithParents(selectedTenantId, selectedOrganizationUnitId, [this.getPermission(form)])
          .pipe(switchMap((ous) => (ous.length ? this.virtualUserService.getVirtualAgents(ous) : of([]))));
      default:
        return of([]);
    }
  }

  public resolveFormTitle(operation: ChatFormOperation): TranslationKeys {
    switch (operation) {
      case ChatFormOperation.Update:
        return TranslationKeys.CompanionVirtualUserUpdateFormTitle;
      case ChatFormOperation.Read:
        return TranslationKeys.CompanionVirtualUserReadFormTitle;
      default:
        return TranslationKeys.CompanionVirtualUserFormTitle;
    }
  }

  public resolveFieldTitle(field: string): TranslationKeys {
    switch (field) {
      case 'name':
        return TranslationKeys.GeneralName;
      case 'tenantId':
        return TranslationKeys.AdminCommonTenant;
      case 'organizationUnitId':
        return TranslationKeys.GeneralOrganizationUnit;
      case 'description':
        return TranslationKeys.GeneralDescription;
      case 'botConfigurationId':
        return TranslationKeys.ManageVirtualUserBotConfiguration;
      case 'botResponseTemplateId':
        return TranslationKeys.GeneralBotResponseTemplate;
      case 'botRequestTemplateId':
        return TranslationKeys.GeneralBotRequestTemplate;
      case 'profile':
        return TranslationKeys.ManageVirtualUserProfile;
      case 'systemInstruction':
        return TranslationKeys.ManageVirtualUserSystemInstruction;
      case 'initialMessage':
        return TranslationKeys.ManageVirtualUserInitialMessage;
      case 'closingMessage':
        return TranslationKeys.ManageVirtualUserClosingMessage;
      case 'sessionTimeoutInSeconds':
        return TranslationKeys.ManageVirtualUserSessionTimeout;
      default:
        return TranslationKeys.GeneralName;
    }
  }

  public resolveFieldAction(field: string, currentValues?: Record<string, unknown>): IChatFormFieldAction | null {
    switch (field) {
      case 'botConfigurationId': {
        const tenantId = currentValues?.tenantId as string;
        const partnerId = currentValues?.partnerId as string;
        const organizationUnitId = currentValues?.organizationUnitId as string;
        if (!tenantId || !organizationUnitId || (this.groupSelectionLookupService.isPartnerRequired && !partnerId)) {
          return null;
        }
        return {
          icon: ICONS.ADD_LIGHT,
          tooltip: TranslationKeys.GeneralCreate,
          routerLink: [...this.organizationUnitLinkService.getLinkByType(LinkType.BotConfiguration).pathFragments, ManageRouteMode.New],
          queryParams: { ...(partnerId ? { partnerId } : {}), tenantId, organizationUnitId },
        };
      }
      default:
        return null;
    }
  }

  public resolveFieldMaxLength(field: string): number | null {
    switch (field) {
      case 'name':
        return this.nameMaxLength;
      default:
        return null;
    }
  }

  private initializeFormValueUpdate(): Observable<IChatFormValueUpdate> {
    return this.botConfigurationService.botConfigurationCreated$.pipe(
      map((bot) => ({
        values: { botConfigurationId: bot.id },
        requiresValues: { organizationUnitId: bot.organizationUnitId },
      })),
    );
  }

  private getPermission(form: IChatForm): NimbusPermission {
    switch (form.operation) {
      case ChatFormOperation.Update:
        return NimbusPermission.VirtualUsersUpdate;
      case ChatFormOperation.Read:
        return NimbusPermission.VirtualUsersRead;
      default:
        return NimbusPermission.VirtualUsersCreate;
    }
  }
}
